/**
 * RemediationPanel — Main container for all remediation guidance display.
 *
 * Shows summary, why it matters, typical fix, metadata (complexity/effort),
 * step-by-step instructions, references, and caution notes.
 */

import type {
  RemediationGuidance,
  FallbackRemediationGuidance,
} from "@accessimonitor/contracts";
import { FallbackNotice } from "./fallback-notice.js";
import { RemediationBadges } from "./remediation-badges.js";
import { RemediationStepsList } from "./remediation-steps-list.js";
import { ReferencesList } from "./references-list.js";
import { CautionNotes } from "./caution-notes.js";

interface RemediationPanelProps {
  guidance: RemediationGuidance | FallbackRemediationGuidance;
  issueKey: string;
  className?: string;
}

export function RemediationPanel({
  guidance,
  issueKey,
  className = "",
}: RemediationPanelProps) {
  const isFallback = "is_fallback" in guidance && guidance.is_fallback;

  return (
    <div className={`remediation-panel ${className}`}>
      {isFallback && <FallbackNotice issueKey={issueKey} />}

      <section className="remediation-section">
        <h4>Summary</h4>
        <p>{guidance.summary}</p>
      </section>

      <section className="remediation-section">
        <h4>Why It Matters</h4>
        <p>{guidance.why_it_matters}</p>
      </section>

      <section className="remediation-section">
        <h4>Typical Fix</h4>
        <p>{guidance.typical_fix_approach}</p>
      </section>

      <RemediationBadges
        ownerRole={guidance.owner_role}
        fixComplexity={guidance.fix_complexity}
        estimatedEffort={guidance.estimated_effort}
      />

      <RemediationStepsList steps={guidance.remediation_steps} />
      <CautionNotes notes={guidance.caution_notes} />
      <ReferencesList
        references={guidance.references}
        wcagCriterion={guidance.wcag_criterion}
      />
    </div>
  );
}
